/**
 * This file includes polyfills needed by Angular and is loaded before the app.
 * You can add your own extra polyfills to this file.
 *
 * This file is divided into 2 sections:
 *   1. Browser polyfills. These are applied before loading ZoneJS and are sorted by browsers.
 *   2. Application imports. Files imported after ZoneJS that should be loaded before your main
 *      file.
 *
 * The current setup is for so-called "evergreen" browsers; the last versions of browsers that
 * automatically update themselves. This includes Safari >= 10, Chrome >= 55 (including Opera),
 * Edge >= 13 on the desktop, and iOS 10 and Chrome on mobile.
 *
 * Learn more in https://angular.io/guide/browser-support
 */

/***************************************************************************************************
 * BROWSER POLYFILLS
 */

/**
 * By default, zone.js will patch all possible macroTask and DomEvents
 * user can disable parts of macroTask/DomEvents patch by setting following flags
 * because those flags need to be set before `zone.js` being loaded, and webpack
 * will put import in the top of bundle, so user need to create a separate file
 * in this directory (for example: zone-flags.ts), and put the following flags
 * into that file, and then add the following code before importing zone.js.
 * import './zone-flags.ts';
 *
 * The flags allowed in zone-flags.ts are listed here.
 *
 * The following flags will work for all browsers.
 *
 * (window as any).__Zone_disable_requestAnimationFrame = true; // disable patch requestAnimationFrame
 * (window as any).__Zone_disable_on_property = true; // disable patch onProperty such as onclick
 * (window as any).__zone_symbol__UNPATCHED_EVENTS = ['scroll', 'mousemove']; // disable patch specified eventNames
 *
 *  in IE/Edge developer tools, the addEventListener will also be wrapped by zone.js
 *  with the following flag, it will bypass `zone.js` patch for IE/Edge
 *
 *  (window as any).__Zone_enable_cross_context_check = true;
 *
 */

/***************************************************************************************************
 * Zone JS is required by default for Angular itself.
 */
import 'zone.js';  // Included with Angular CLI.

/**
 * zone.js patches `EventTarget.prototype.addEventListener` and, when the caller passes
 * `{ once: true }`, writes `options.once = false` directly into the caller's own options
 * object instead of copying it (zone.js/fesm2015/zone.js:1923 — it does this deliberately
 * to avoid an allocation on a hot path).
 *
 * Yellowfin's JsAPI (`${yellowfinSetting.url}/JsAPI/v3`) passes an options object whose
 * `once` is a getter-only accessor, so that write throws
 * `TypeError: Cannot set property once of [object Object] which has only a getter`,
 * the listener is never attached, and the surrounding Yellowfin module aborts mid-init.
 *
 * Handing zone.js a writable shallow copy avoids the throw. Only objects that actually
 * carry a truthy `once` are copied, so ordinary boolean / `{ passive: true }` calls are
 * untouched. This is safe because zone.js restores `once` on the object it received and
 * `removeEventListener` matches on delegate + capture rather than options identity.
 */
const zoneAddEventListener = EventTarget.prototype.addEventListener;
EventTarget.prototype.addEventListener = function (
	type: string,
	listener: EventListenerOrEventListenerObject | null,
	options?: boolean | AddEventListenerOptions
): void {
	if ( options && typeof options === 'object' && options.once ) {
		options = { ...options };
	}
	return zoneAddEventListener.call( this, type, listener, options );
};


/**
 * Yellowfin's JsAPI bundle (`dist/commonEntryChunk.bundle.*.js`, webpack module `vbyB`)
 * overwrites two standard String methods with broken implementations, and — unlike the
 * `includes` / `endsWith` / `Element.matches` polyfills sitting right next to them — does
 * it with no `if (!String.prototype.x)` guard:
 *
 *   String.prototype.replaceAll = function (e, t) { return null == t ? this : this.split(e).join(t) }
 *   String.prototype.startsWith = function (e) { return 0 == this.indexOf(e) }
 *
 * `split().join()` cannot invoke a function replacer — `Array.join` stringifies it — so
 * `str.replaceAll(re, m => ...)` substitutes the replacer's own source text. PrimeNG 20
 * resolves every design token that way (`"{a.b}".replaceAll(/{[^}]*}/g, m => `var(--p-a-b)`)`),
 * so each component whose stylesheet is generated while Yellowfin is loaded emits garbage
 * such as `border-width: o=>;`. PrimeNG injects and caches that stylesheet once per
 * component, keyed by `data-primeng-style-id`, so the damage is permanent for the browser
 * session and survives SPA navigation. `startsWith` separately loses its position argument
 * (`'abc'.startsWith('b', 1)` returns false instead of true).
 *
 * Assigning the correct methods here is not enough on its own: the JsAPI loads its bundles
 * asynchronously, long after this file runs, and would overwrite them again. A non-writable
 * property would make their assignment throw inside a strict-mode bundle. Accessors whose
 * setter swallows the write keep the correct implementation without breaking Yellowfin.
 *
 * `replaceAll` is restored as a strict superset of the spec — identical for every input that
 * already behaves correctly, lenient only for a non-global RegExp, which the native method
 * rejects and Yellowfin itself relies on (`.replaceAll(/\/\*[\s\S]*?\*\//, '')`).
 */
const nativeReplaceAll = ( String.prototype as any ).replaceAll as ( ...args: any[] ) => string;
const nativeStartsWith = String.prototype.startsWith;

const replaceAllShim = function ( this: string, pattern: any, replacement: any ): string {
	if ( pattern instanceof RegExp && !pattern.global ) {
		return String.prototype.replace.call( this, pattern, replacement );
	}
	return nativeReplaceAll.call( this, pattern, replacement );
};

const defineUnclobberableStringMethod = ( name: string, implementation: unknown ): void => {
	Object.defineProperty( String.prototype, name, {
		configurable: true,
		get: () => implementation,
		set: () => { /* ignore third-party overwrites */ },
	} );
};

defineUnclobberableStringMethod( 'replaceAll', replaceAllShim );
defineUnclobberableStringMethod( 'startsWith', nativeStartsWith );

/***************************************************************************************************
 * APPLICATION IMPORTS
 */
